/**
 * Resolve the UILM `moduleId` that owns a given template's translation
 * keys. Used at the start of any template-level sync flow.
 *
 * Strategy: the module name is deterministic (slug → camelCase), so
 * `Module/Gets` is the single source of truth.
 *   1. List modules and look for one with our derived name.
 *   2. If found, return its itemId.
 *   3. If not, create it via `Module/Save` and refetch to get the itemId.
 *
 * Why not use `GetsByKeyNames` to short-circuit step 1? Because between
 * "module created" and "first key saved" there are zero keys, so the
 * discovery returns empty and the caller would re-trigger module
 * creation — which then fails on the unique-name guard. Always going
 * through `Module/Gets` makes the function safely idempotent for any
 * caller, including concurrent ones (diff hook + seed click).
 */

import { getModule } from '@/components/core/language-selector/services/language.service';
import { saveUilmModule } from '../services/uilm-admin.service';
import { slugToCamelCase } from '@/i18n/utils/slug-to-camel-case';

/**
 * Session-scoped slug → moduleId cache.
 *
 * The moduleId is immutable for the lifetime of a UILM module (it's the
 * UDS ItemId assigned on insert), so once we've resolved it for a slug
 * we can reuse it for every subsequent sync in this page session without
 * hitting `Module/Gets` again. Cache clears on page reload, which is
 * fine — the lookup is correct on cold start.
 */
const moduleIdCache = new Map<string, string>();

export async function getOrCreateTemplateModule(templateSlug: string): Promise<string> {
  const cached = moduleIdCache.get(templateSlug);
  if (cached) return cached;

  const moduleName = slugToCamelCase(templateSlug);

  // 1. Canonical source of truth: the module list.
  const initial = await getModule();
  const existing = initial.find((m) => m.moduleName === moduleName);
  if (existing) {
    moduleIdCache.set(templateSlug, existing.itemId);
    return existing.itemId;
  }

  // 2. Not there — create it. `Module/Save` rejects duplicate names, so
  //    this only runs the first time we sync this template.
  await saveUilmModule(moduleName);

  // 3. Refetch to grab the freshly assigned itemId. Selise UILM creates
  //    modules synchronously so this read-after-write is safe.
  const after = await getModule();
  const created = after.find((m) => m.moduleName === moduleName);
  if (!created) {
    throw new Error(
      `UILM module "${moduleName}" was created but cannot be found in Module/Gets`,
    );
  }
  moduleIdCache.set(templateSlug, created.itemId);
  return created.itemId;
}

/** Forget a cached entry. Call when a template is deleted or renamed. */
export function clearTemplateModuleCache(templateSlug?: string): void {
  if (templateSlug) moduleIdCache.delete(templateSlug);
  else moduleIdCache.clear();
}
